import type {
  FilterSettings,
  RangeValue,
} from "@/components/ui/search-filter-bar";
import {
  parseISO,
  isValid,
  isAfter,
  isBefore,
  isWithinInterval,
} from "date-fns";
import { getNestedValue } from "@employee-management/common";

export function filterData<T extends Record<string, any>>(
  data: T[],
  filterSettings: FilterSettings,
  searchFields: (keyof T)[]
): T[] {
  let filtered = [...data];

  // Apply search term
  if (filterSettings.searchTerm.trim()) {
    const searchLower = filterSettings.searchTerm.toLowerCase();
    filtered = filtered.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(searchLower);
      })
    );
  }

  // Apply filters
  Object.entries(filterSettings.filters).forEach(([key, value]) => {
    if (value && value !== "all") {
      filtered = filtered.filter((item) => {
        const itemValue = getNestedValue(item, key);
        if (itemValue == null) return false;

        // Handle range values
        if (typeof value === "object" && "min" in value && "max" in value) {
          const rangeValue = value as RangeValue;
          return filterByRange(itemValue, rangeValue);
        }

        // Handle boolean values
        if (typeof itemValue === "boolean") {
          return itemValue === (value === "true");
        }

        return String(itemValue) === value;
      });
    }
  });

  return filtered;
}

function filterByRange(itemValue: any, rangeValue: RangeValue): boolean {
  // Handle date ranges
  if (rangeValue.min || rangeValue.max) {
    // Try to parse as date first
    let itemDate: Date | null = null;

    // Handle different date formats
    if (typeof itemValue === "string") {
      // Try ISO format first
      itemDate = parseISO(itemValue);
      if (!isValid(itemDate)) {
        // Try direct Date constructor
        itemDate = new Date(itemValue);
        if (!isValid(itemDate)) {
          // If still not valid, try as number
          return filterByNumberRange(Number(itemValue), rangeValue);
        }
      }
    } else if (itemValue instanceof Date) {
      itemDate = itemValue;
    } else if (typeof itemValue === "number") {
      return filterByNumberRange(itemValue, rangeValue);
    } else {
      // Try to convert to number
      return filterByNumberRange(Number(itemValue), rangeValue);
    }

    if (itemDate && isValid(itemDate)) {
      const minDate = rangeValue.min ? parseISO(rangeValue.min) : null;
      const maxDate = rangeValue.max ? parseISO(rangeValue.max) : null;

      if (minDate && maxDate && isValid(minDate) && isValid(maxDate)) {
        return isWithinInterval(itemDate, { start: minDate, end: maxDate });
      } else if (minDate && isValid(minDate)) {
        return (
          isAfter(itemDate, minDate) || itemDate.getTime() === minDate.getTime()
        );
      } else if (maxDate && isValid(maxDate)) {
        return (
          isBefore(itemDate, maxDate) ||
          itemDate.getTime() === maxDate.getTime()
        );
      }
    }
  }

  return true;
}

function filterByNumberRange(
  itemValue: number,
  rangeValue: RangeValue
): boolean {
  if (isNaN(itemValue)) return false;

  const min = rangeValue.min ? Number(rangeValue.min) : null;
  const max = rangeValue.max ? Number(rangeValue.max) : null;

  if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
    return itemValue >= min && itemValue <= max;
  } else if (min !== null && !isNaN(min)) {
    return itemValue >= min;
  } else if (max !== null && !isNaN(max)) {
    return itemValue <= max;
  }

  return true;
}
